/**
 * InviteAcceptPage — shared set-password flow used by three callers:
 *   - brand member invite       (variant 'invite')
 *   - admin team member invite   (variant 'invite')
 *   - brand owner activation     (variant 'activation')
 *
 * Both variants auto-sign-in via a Firebase custom token returned by acceptFn
 * and signInWithToken. 'invite' additionally collects first/last name;
 * 'activation' skips those fields (the owner already has a name).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Loader2, XCircle, ShieldCheck, UserCheck, AlertCircle } from '../../icons';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { usePasswordPolicy } from './usePasswordPolicy';
import { PasswordRequirements } from './PasswordRequirements';
import { GoogleGlyph } from './GoogleGlyph';

export interface InviteVerifyResponse {
  email: string;
  [key: string]: unknown;
}

export interface InviteAcceptBody {
  token: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export interface InviteAcceptResponse {
  firebase_custom_token: string;
}

type Variant = 'invite' | 'activation';

export interface InviteAcceptPageProps {
  /** Verify the token — returns metadata (email, org name, role). */
  verifyFn: (token: string) => Promise<InviteVerifyResponse>;
  /**
   * Submit the new password. For 'invite' returns the Firebase custom token;
   * for 'activation' returns void (the owner logs in afterward).
   */
  acceptFn: (body: InviteAcceptBody) => Promise<InviteAcceptResponse | undefined>;
  /** Key on the verify response that holds the org name ("brand_name" or "team_name"). */
  orgNameKey: string;
  /** App handles Firebase signInWithCustomToken and calls this on success. Required for 'invite'. */
  signInWithToken?: (token: string) => Promise<void>;
  /**
   * Accepts the invite via a Google sign-in the app already completed
   * (popup + sign-out + BE call + signInWithToken, mirroring handleSubmit's
   * password path). Throws on failure (e.g. email mismatch) with a message
   * this component surfaces via the shared error area. Omit to hide the
   * "Continue with Google" option — 'activation' doesn't offer it, since the
   * account owner already exists.
   */
  onGoogleAccept?: () => Promise<void>;
  /** Where to navigate after success. Defaults to '/' ('invite') or '/login' ('activation'). */
  successRedirect?: string;
  /** 'invite' (default) collects a name + auto-signs-in; 'activation' just sets the password. */
  variant?: Variant;
  /**
   * Single-use set-password token. Supplied by the route component from its
   * typed `Route.useSearch()`. Falls back to `window.location.search` only as
   * a defensive default for any non-routed consumer.
   */
  token?: string;
  /**
   * Email of the Firebase user currently signed in in this browser, if any.
   * Three states matter: `undefined` = still resolving (don't decide yet,
   * a session might turn out to be active), `null` = confirmed no session,
   * a string = that email is signed in. Sourced from the caller's `useAuth()`
   * so this component stays Firebase-agnostic.
   */
  currentUserEmail?: string | null;
  /** Signs out the current session. Required whenever currentUserEmail may be set. */
  onSignOutCurrentUser?: () => Promise<void>;
}

type State = 'loading' | 'form' | 'submitting' | 'success' | 'error' | 'rate-limited';

interface Copy {
  loadingTitle: string;
  formTitle: string;
  formSubtitle: (org: string) => string;
  emailLabel: string;
  submitIdle: string;
  submitBusy: string;
  googleIdle: string;
  successTitle: string;
  successBody: string;
  errorTitle: string;
  footerExpiry: string;
  expiredMsg: string;
  invalidMsg: string;
}

const COPY: Record<Variant, Copy> = {
  invite: {
    loadingTitle: 'Verifying your invite…',
    formTitle: "You're invited",
    formSubtitle: (org) => `Join ${org} on Discovr`,
    emailLabel: 'Invite for',
    submitIdle: 'Accept Invite & Set Password',
    submitBusy: 'Joining…',
    googleIdle: 'Continue with Google',
    successTitle: 'Welcome aboard!',
    successBody: 'Your account is set up. Redirecting…',
    errorTitle: 'Invite unavailable',
    footerExpiry: 'Invite links expire after 7 days.',
    expiredMsg: 'This invite has expired.',
    invalidMsg: 'This invite link is invalid.',
  },
  activation: {
    loadingTitle: 'Verifying your link…',
    formTitle: 'Set your password',
    formSubtitle: (org) => `Activate your ${org} account on Discovr`,
    emailLabel: 'Account',
    submitIdle: 'Set Password',
    submitBusy: 'Setting password…',
    googleIdle: 'Continue with Google',
    successTitle: 'Password set!',
    successBody: 'Your account is ready. Redirecting…',
    errorTitle: 'Link unavailable',
    footerExpiry: 'Activation links expire after 7 days.',
    expiredMsg: 'This link is no longer valid. Ask an admin to resend it.',
    invalidMsg: 'This activation link is invalid.',
  },
};

export function InviteAcceptPage({
  verifyFn,
  acceptFn,
  orgNameKey,
  signInWithToken,
  onGoogleAccept,
  successRedirect,
  variant = 'invite',
  token: tokenProp,
  currentUserEmail,
  onSignOutCurrentUser,
}: InviteAcceptPageProps) {
  const copy = COPY[variant];
  const collectName = variant === 'invite';
  const redirectTo = successRedirect ?? (variant === 'activation' ? '/login' : '/');

  const navigate = useNavigate();
  const token =
    tokenProp ?? new URLSearchParams(window.location.search).get('token') ?? '';

  const [state, setState] = useState<State>(!token ? 'error' : 'loading');
  const [errorMsg, setErrorMsg] = useState(!token ? 'This link is missing a token.' : '');
  const [invite, setInvite] = useState<InviteVerifyResponse | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [googleBusy, setGoogleBusy] = useState(false);
  const [sessionAcknowledged, setSessionAcknowledged] = useState(false);
  const [signingOutSession, setSigningOutSession] = useState(false);

  const authResolved = currentUserEmail !== undefined;
  const showSessionWarning =
    authResolved &&
    !sessionAcknowledged &&
    currentUserEmail !== null &&
    !!invite &&
    currentUserEmail !== invite.email;

  const handleSignOutAndContinue = async () => {
    setSigningOutSession(true);
    try {
      await onSignOutCurrentUser?.();
    } finally {
      setSessionAcknowledged(true);
      setSigningOutSession(false);
    }
  };

  // This flow sets the password through the backend (Admin SDK), which bypasses the
  // Firebase password policy and enforces its own `min=8` — floor the checklist to
  // match so a 6–7 char password can't pass here only to be rejected by the server.
  const {
    requirements,
    isValid: isPasswordValid,
    errorMessage: passwordError,
  } = usePasswordPolicy(password, { minLength: 8 });

  const ran = useRef(false);
  const navigatedRef = useRef(false);

  // On success: for the 'invite' variant (which signs in via a custom token),
  // wait for currentUserEmail to actually reflect the new account before
  // navigating — a flat timer risks landing in the protected shell before the
  // new session has propagated. 'activation' has no client-side sign-in to
  // wait for, so it keeps the original short, fixed delay. Either way, a
  // fallback timer guards against currentUserEmail never converging.
  useEffect(() => {
    if (state !== 'success' || navigatedRef.current) return;

    if (!signInWithToken) {
      const t = setTimeout(() => {
        navigatedRef.current = true;
        void navigate({ to: redirectTo });
      }, 1000);
      return () => clearTimeout(t);
    }

    if (currentUserEmail === invite?.email) {
      navigatedRef.current = true;
      void navigate({ to: redirectTo });
      return;
    }

    const fallback = setTimeout(() => {
      navigatedRef.current = true;
      void navigate({ to: redirectTo });
    }, 5000);
    return () => clearTimeout(fallback);
  }, [state, signInWithToken, currentUserEmail, invite, redirectTo, navigate]);

  useEffect(() => {
    if (ran.current || !token) return;
    ran.current = true;
    verifyFn(token)
      .then((data) => {
        setInvite(data);
        setState('form');
      })
      .catch((e: { httpStatus?: number; message?: string }) => {
        if (e.httpStatus === 410) {
          setErrorMsg(copy.expiredMsg);
          setState('error');
        } else if (e.httpStatus === 401 || e.httpStatus === 404) {
          setErrorMsg(copy.invalidMsg);
          setState('error');
        } else if (e.httpStatus === 429) {
          // Rate-limited, NOT a dead link: the verify GET and the accept POST
          // share an IP-scoped rate-limit bucket on the BE, so a few failed
          // password submits can exhaust the budget and 429 the very next
          // verify call (page refresh, reopening the emailed link) even
          // though the underlying token was never touched. Collapsing this
          // into the terminal 'error' state would make a perfectly valid
          // link look permanently dead — stay non-terminal so the same link
          // keeps working once the window clears.
          setErrorMsg('Too many attempts — please wait a moment and refresh this page.');
          setState('rate-limited');
        } else {
          setErrorMsg(e.message ?? copy.invalidMsg);
          setState('error');
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (collectName) {
      if (!trimmedFirst) {
        setFormError('Please enter your first name.');
        return;
      }
      if (!trimmedLast) {
        setFormError('Please enter your last name.');
        return;
      }
    }
    if (!isPasswordValid) {
      setFormError(passwordError ?? 'Please meet all password requirements.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setState('submitting');
    try {
      const result = await acceptFn({
        token,
        password,
        ...(collectName ? { first_name: trimmedFirst, last_name: trimmedLast } : {}),
      });
      if (signInWithToken && result && 'firebase_custom_token' in result) {
        // The shared AuthProvider gate verifies POST /auth/login { role: appSurface }
        // after this Firebase sign-in and only exposes `user` if the account is
        // authorized for the app — so no explicit /auth/login call is needed here.
        await signInWithToken(result.firebase_custom_token);
      }
      setState('success');
    } catch (e: unknown) {
      setFormError(
        (e as { message?: string }).message ?? 'Something went wrong. Please try again.',
      );
      setState('form');
    }
  };

  const handleGoogleAccept = async () => {
    if (!onGoogleAccept) return;
    setFormError('');
    setGoogleBusy(true);
    try {
      await onGoogleAccept();
      setState('success');
    } catch (e: unknown) {
      setFormError(
        (e as { message?: string }).message ?? 'Could not accept the invite with Google. Please try again.',
      );
    } finally {
      setGoogleBusy(false);
    }
  };

  const orgName = invite ? String(invite[orgNameKey] ?? '') : '';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-muted px-4 py-12">
      <div className="w-full max-w-md rounded-md border border-border bg-card">
        <div className="rounded-t-md bg-black px-8 py-6">
          <span className="text-xl font-extrabold tracking-tight text-white">Discovr</span>
        </div>

        <div className="p-8">
          {(state === 'loading' || ((state === 'form' || state === 'submitting') && invite && !authResolved)) && (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex size-20 items-center justify-center rounded-full bg-secondary">
                <Loader2 size={36} className="animate-spin text-muted-foreground" />
              </div>
              <h1 className="text-heading-h6 text-foreground">{copy.loadingTitle}</h1>
              <p className="text-body-sm-regular text-muted-foreground">This only takes a second.</p>
            </div>
          )}

          {(state === 'form' || state === 'submitting') && invite && authResolved && showSessionWarning && (
            <div className="flex flex-col items-center gap-5 text-center">
              <div className="flex size-14 items-center justify-center rounded-full bg-amber-50">
                <AlertCircle size={26} className="text-amber-700" />
              </div>
              <div>
                <h1 className="text-heading-h6 text-foreground">You're signed in as {currentUserEmail}</h1>
                <p className="mt-2 text-body-sm-regular leading-relaxed text-muted-foreground">
                  This invite is for <span className="font-medium text-foreground">{invite.email}</span>. Sign out to
                  continue setting up their account.
                </p>
              </div>
              <Button
                type="button"
                disabled={signingOutSession}
                onClick={() => {
                  void handleSignOutAndContinue();
                }}
                className="w-full"
              >
                {signingOutSession ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Signing out…
                  </>
                ) : (
                  'Sign out & Continue'
                )}
              </Button>
            </div>
          )}

          {(state === 'form' || state === 'submitting') && invite && authResolved && !showSessionWarning && (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="flex size-14 items-center justify-center rounded-full bg-success/10">
                  <UserCheck size={26} className="text-success" />
                </div>
                <div>
                  <h1 className="text-heading-h6 text-foreground">{copy.formTitle}</h1>
                  {orgName && (
                    <p className="mt-1 text-body-sm-regular text-muted-foreground">{copy.formSubtitle(orgName)}</p>
                  )}
                </div>
              </div>

              <div className="rounded-md border border-border bg-muted px-4 py-3">
                <p className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                  {copy.emailLabel}
                </p>
                <p className="mt-0.5 text-body-sm-medium text-foreground">{invite.email}</p>
              </div>

              <form
                onSubmit={(e) => {
                  void handleSubmit(e);
                }}
                className="flex flex-col gap-4"
              >
                {/* First + Last name (invite only) */}
                {collectName && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="invite-first-name"
                        className="text-caption-medium text-muted-foreground"
                      >
                        First name
                      </label>
                      <input
                        id="invite-first-name"
                        type="text"
                        autoComplete="given-name"
                        placeholder="Jane"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={state === 'submitting'}
                        className="rounded-md border border-border bg-background px-3.5 py-2.5 text-body-sm-regular text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/30 focus:outline-none disabled:opacity-50"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="invite-last-name"
                        className="text-caption-medium text-muted-foreground"
                      >
                        Last name
                      </label>
                      <input
                        id="invite-last-name"
                        type="text"
                        autoComplete="family-name"
                        placeholder="Smith"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={state === 'submitting'}
                        className="rounded-md border border-border bg-background px-3.5 py-2.5 text-body-sm-regular text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/30 focus:outline-none disabled:opacity-50"
                      />
                    </div>
                  </div>
                )}

                {/* Password */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="invite-password" className="text-caption-medium text-muted-foreground">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="invite-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={state === 'submitting'}
                      className="w-full rounded-md border border-border bg-background px-3.5 py-2.5 pr-10 text-body-sm-regular text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/30 focus:outline-none disabled:opacity-50"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                      className="absolute inset-y-0 right-0 flex items-center px-3.5 text-muted-foreground hover:bg-transparent hover:text-foreground"
                    >
                      {showPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M9.88 9.88 12 12" />
                          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                          <path d="M6.61 6.61A13.52 13.52 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                          <line x1="2" x2="22" y1="2" y2="22" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </Button>
                  </div>
                  {password.length > 0 && (
                    <PasswordRequirements requirements={requirements} className="mt-1.5" />
                  )}
                </div>

                {/* Confirm password */}
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="invite-confirm-password"
                    className="text-caption-medium text-muted-foreground"
                  >
                    Confirm Password
                  </label>
                  <Input
                    id="invite-confirm-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={state === 'submitting'}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={state === 'submitting' || !isPasswordValid}
                  className="mt-1 w-full"
                >
                  {state === 'submitting' ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      {copy.submitBusy}
                    </>
                  ) : (
                    copy.submitIdle
                  )}
                </Button>
              </form>

              {formError && (
                <p
                  role="alert"
                  className="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-2.5 text-body-sm-regular text-destructive"
                >
                  {formError}
                </p>
              )}

              {onGoogleAccept && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-caption-regular text-muted-foreground">or</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      void handleGoogleAccept();
                    }}
                    disabled={state === 'submitting' || googleBusy}
                    className="h-11 w-full rounded-full border-border text-body-md-medium text-foreground hover:bg-muted disabled:opacity-50"
                  >
                    {googleBusy ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <GoogleGlyph size={16} className="mr-2" />
                    )}
                    {copy.googleIdle}
                  </Button>
                </div>
              )}
            </div>
          )}

          {state === 'success' && (
            <div className="flex flex-col items-center gap-5 text-center">
              <div className="flex size-20 items-center justify-center rounded-full bg-success/10">
                <UserCheck size={36} className="text-success" />
              </div>
              <div>
                <h1 className="text-heading-h6 text-foreground">{copy.successTitle}</h1>
                <p className="mt-2 text-body-sm-regular leading-relaxed text-muted-foreground">{copy.successBody}</p>
              </div>
              <Loader2 size={18} className="animate-spin text-muted-foreground" />
            </div>
          )}

          {state === 'error' && (
            <div className="flex flex-col items-center gap-5 text-center">
              <div className="flex size-20 items-center justify-center rounded-full bg-destructive/10">
                <XCircle size={36} className="text-destructive" />
              </div>
              <div>
                <h1 className="text-heading-h6 text-foreground">{copy.errorTitle}</h1>
                <p className="mt-2 text-body-sm-regular leading-relaxed text-muted-foreground">{errorMsg}</p>
              </div>
              <a
                href="/"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-body-sm-semibold text-primary-foreground hover:bg-primary/90"
              >
                Back to homepage
              </a>
            </div>
          )}

          {/* Non-terminal: the link itself is still valid, just temporarily
              rate-limited — offer a refresh instead of the dead-end 'error'
              treatment above (no "Back to homepage" implying the link died). */}
          {state === 'rate-limited' && (
            <div className="flex flex-col items-center gap-5 text-center">
              <div className="flex size-20 items-center justify-center rounded-full bg-amber-50">
                <AlertCircle size={36} className="text-amber-700" />
              </div>
              <div>
                <h1 className="text-heading-h6 text-foreground">Please wait a moment</h1>
                <p className="mt-2 text-body-sm-regular leading-relaxed text-muted-foreground">{errorMsg}</p>
              </div>
              <Button type="button" onClick={() => window.location.reload()} className="w-full max-w-[200px]">
                Refresh
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-start gap-3 rounded-b-md border-t border-border bg-muted px-8 py-4">
          <ShieldCheck size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
          <div className="text-caption-regular leading-relaxed text-muted-foreground">
            <p>{copy.footerExpiry}</p>
            <p>
              Need help?{' '}
              <a
                href="mailto:hello@getdiscovr.ai"
                className="font-semibold text-foreground hover:text-foreground/80"
              >
                Contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
